package com.heelion.farmafreelance.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.heelion.farmafreelance.models.Professione
import com.heelion.farmafreelance.models.etichetta

/** Colore di ogni professione, uguale in tutta l'app: blu per i farmacisti, viola per gli infermieri
 * (non verde: la croce verde è l'insegna delle farmacie; non rossa: è l'emblema della Croce Rossa). */
val Professione.colore: Color
    get() = when (this) {
        Professione.FARMACISTA -> Color(0xFF1565C0)
        Professione.INFERMIERE -> Color(0xFF7B1FA2)
    }

/** Il caduceo per i farmacisti, la croce per gli infermieri. */
val Professione.icona: ImageVector
    get() = when (this) {
        Professione.FARMACISTA -> AppIcons.Caduceo
        Professione.INFERMIERE -> AppIcons.Croce
    }

/** "Farmacisti" / "Infermieri", per il selettore. */
val Professione.plurale: String
    get() = when (this) {
        Professione.FARMACISTA -> "Farmacisti"
        Professione.INFERMIERE -> "Infermieri"
    }

/** Etichetta piccola con simbolo e colore della professione, da mettere sulle schede di turni e proposte. */
@Composable
fun EtichettaProfessione(professione: Professione, modifier: Modifier = Modifier) {
    Surface(color = professione.colore.copy(alpha = 0.12f), shape = RoundedCornerShape(50), modifier = modifier) {
        Row(modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp), verticalAlignment = Alignment.CenterVertically) {
            Icon(professione.icona, contentDescription = null, tint = professione.colore, modifier = Modifier.size(12.dp))
            Spacer(Modifier.width(4.dp))
            Text(
                professione.etichetta.uppercase(),
                style = MaterialTheme.typography.labelSmall,
                fontWeight = FontWeight.Bold,
                color = professione.colore
            )
        }
    }
}

/**
 * [ Farmacisti | Infermieri ] in cima all'app della farmacia che lavora anche con gli infermieri:
 * Home, turni pubblicati e ricerca mostrano solo la professione scelta. Il numerino sull'altra
 * voce avvisa se lì c'è qualcosa da guardare, così non si perde niente.
 */
@Composable
fun SelettoreProfessione(viewModel: AuthViewModel, modifier: Modifier = Modifier) {
    Row(modifier = modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        Professione.entries.forEach { p ->
            val scelta = viewModel.professioneVisibile == p
            val daGuardare = viewModel.daGuardare(p)
            Card(
                onClick = { viewModel.scegliProfessione(p) },
                modifier = Modifier.weight(1f),
                shape = RoundedCornerShape(14.dp),
                colors = CardDefaults.cardColors(containerColor = if (scelta) p.colore else Color.White),
                border = BorderStroke(1.5.dp, p.colore),
                elevation = CardDefaults.cardElevation(defaultElevation = if (scelta) 3.dp else 0.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(vertical = 10.dp, horizontal = 12.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center
                ) {
                    val testo = if (scelta) Color.White else p.colore
                    Icon(p.icona, contentDescription = null, tint = testo, modifier = Modifier.size(20.dp))
                    Spacer(Modifier.width(8.dp))
                    Text(p.plurale, fontWeight = FontWeight.Bold, color = testo)
                    if (!scelta && daGuardare > 0) {
                        Spacer(Modifier.width(6.dp))
                        Surface(color = Color(0xFFD32F2F), shape = RoundedCornerShape(50)) {
                            Text(
                                daGuardare.toString(),
                                modifier = Modifier.padding(horizontal = 6.dp),
                                style = MaterialTheme.typography.labelSmall,
                                fontWeight = FontWeight.Bold,
                                color = Color.White
                            )
                        }
                    }
                }
            }
        }
    }
}
